import { useMatch, useNavigate, useParams } from "react-router-dom";
import {
  Card,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
  Box,
  CircularProgress,
  ImageList,
  ImageListItem,
  Button,
  Dialog,
  IconButton,
  DialogContent,
  CardContent,
  Chip,
} from "@mui/material";
import PersonOutline from "@iconify/icons-eva/person-outline";
import DeleteIcon from "@iconify/icons-eva/minus-fill";
import AddIcon from "@iconify/icons-eva/plus-fill";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import { is } from "src/util/is";
import { BaseTable } from "../BaseTable/BaseTable";
import {
  teamsFormTabs,
  getTeamDefaultValues,
  teamMembersColumns,
  possibleBusinessTypes,
  initialPayoutAccountObj,
  initialFinanceConfigObj,
  subscriptionDetailsColumns,
} from "./teams.constants";
import { Form, FormikProvider, useFormik } from "formik";
import {
  useGetDetail,
  useMutate,
  usePaymentDetailsOfCompany,
} from "src/hooks/useFetch.hook";
import { teamUrl, teamUrlId, teamChargerLicenseStatusUrl } from "src/react-query/endPoints";
import { useAccess } from "src/hooks/useAccess.hook";
import { Loader } from "../Loader";
import { LoadingButton, TabContext, TabPanel } from "@mui/lab";
import { queryKeys } from "src/react-query/queryKeys";
import { fetchWithError } from "src/react-query/fetchWithError";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { selector } from "src/react-query/select";
import enums, { FEATURE } from "src/util/enums";
import Address from "src/common/components/Address";
import { teamFormValidationSchema } from "src/util/validationSchemas";
import { RootTabItemStyles, RootTabStyle } from "src/theme/overrides/Tabs";
import { Icon } from "@iconify/react";
import { dialogSlice } from "src/store/dailog/dailog.slice";
import { useDispatch, useSelector } from "react-redux";
import { TeamUserModal } from "./TeamUserModal";
import { alertSlice } from "src/store/alert/alert.slice";
import { isObjectWithNonNullValues } from "src/utils/utility";
import { useSelectedTeam } from "src/hooks/useSelectedTeam";
import TeamFinanceForm from "./TeamFinanceForm";
import { tabsCurrentlyOpenedForSlice } from "src/store/tabsOpened/tabsOpened.slice";
import { useMutation } from "@tanstack/react-query";
import { fileToBase64 } from "src/utils/fileConvertBase64";
import {
  issueImgUpload,
  issueSubmission,
} from "../ServiceRequest/serviceRequest.hook";
import ImgPreviewModal from "src/components/ImagePreviewModal/ImgPreviewModal";
import { GridCloseIcon } from "@mui/x-data-grid";
import sanitizeValues from "src/utils/sanitizeValues";
import { isValidImageType, MAX_IMAGE_SIZE_MB } from "src/utils/fileTypes";
import SubscriptionDetailsModal from "./SubscriptionDetailsModal";
import BulkNotificationModal from "./BulkNotificationModal";
import AddLicenseModal from "./AddLicenseModal";

// import TeamPayoutInvoices from "./TeamPayoutInvoices";
import TeamPayoutScheduleTab from "./TeamPayoutScheduleTab";
import CompanyEnableFeatures from "../company/CompanyEnableFeatures";
import Communicate from "../company/Communicate";
import TeamPayoutAccountEditModal from "./TeamPayoutAccountEditModal";
import { communicationDefaultValue } from "../company/company.constants";

export const TeamForm = () => {
  // const [chargemilesPayoutIdDisabled,setChargemilesPayoutIdDisabled] = useState(true);
  const { teamId: parentCompanyId } = useSelectedTeam();
  const [selectedTab, setSelectedTab] = useState("general");
  const [addPayoutAccounts, setAddPayoutAccounts] = useState(false);
  const { id } = useParams();
  const { open: modalOpen } = useSelector(({ dialog }) => dialog);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const {
    actions: { setAlert },
  } = alertSlice;
  const isCreateTeam = useMatch("/team/new");
  const hasReadOnlyAccess = useAccess(FEATURE.TEAM, "R");
  const canEditFinanceConfig = useAccess(FEATURE.FINANCE, "FA");
  const canViewFinanceConfig = useAccess(FEATURE.FINANCE, "V");
  const [imageUploadPending, setImageUploadPending] = useState(false);
  const [uploadedImgUrl, setUploadedImageUrl] = useState(false);
  const imageUploadInputRef = useRef();
  const imgUpload = useMutation(issueImgUpload);
  const submission = useMutation(issueSubmission);
  const [photoModal, setPhotoModal] = useState({ open: false, src: "" });
  const [payoutAccountModalState, setPayoutAccountModalState] = useState({
    open: false,
    index: null,
  });

  const isReadOnly = hasReadOnlyAccess && !isCreateTeam;
  const financeFormPermissions = { isReadOnly: isReadOnly || !canEditFinanceConfig };

  const [teamUsersData, setTeamUsers] = useState([]);
  const [subscriptionData, setSubscriptionData] = useState([]);
  const [isSubscriptionLoading, setIsSubscriptionLoading] = useState(false);
  const [subscriptionModalOpen, setSubscriptionModalOpen] = useState(false);
  const [selectedSubscriptionData, setSelectedSubscriptionData] = useState(null);
  const [bulkNotificationModalOpen, setBulkNotificationModalOpen] = useState(false);
  const [addLicenseModalOpen, setAddLicenseModalOpen] = useState(false);

  const {
    data: teamUsers,
    isFetching: isUserFetching,
    refetch: refetchMembers,
    isRefetching: isRefetchingMembers,
  } = useGetDetail("usersByTeam", { id });

  const { data: company } = useGetDetail("company", { id: parentCompanyId });
  const [taxLabel, setTaxLabel] = useState(
    (company && company?.taxBillingConfig?.taxLabel) || "Tax"
  );
  const userCompany = useSelector((state) => state.auth.user?.company);

  const isParentCompanyLogin = userCompany?.companyType === "P";
  const isGPCLogin = userCompany?.companyType === "GP";

  const tabsToShow = useMemo(() => {
    let updatedTeamsFormTab = [...teamsFormTabs];
    if (isParentCompanyLogin) {
      updatedTeamsFormTab = updatedTeamsFormTab.filter(
        (tab) => tab.value !== "finance" && tab.value !== "payout schedule"
      );
    }

    if (!canViewFinanceConfig) {
      updatedTeamsFormTab = updatedTeamsFormTab.filter(
        (tab) => tab.value !== "finance"
      );
    }

    if (!canEditFinanceConfig) {
      updatedTeamsFormTab = updatedTeamsFormTab.filter(
        (tab) => tab.value !== "payout schedule"
      );
    }

    if (id) {
      return updatedTeamsFormTab;
    }
    return updatedTeamsFormTab.filter((tab) => tab.value !== "members" && tab.value !== "subscription" && tab.value !== "payout schedule");
  }, [id, isParentCompanyLogin, canEditFinanceConfig, canViewFinanceConfig]);

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    if (!isValidImageType(file.type)) {
      dispatch(
        setAlert({
          open: true,
          message: `Only JPEG, JPG, PNG, WEBP or SVG images are allowed.`,
          severity: enums.SEVERITY.ERROR,
        })
      );
      return;
    }

    // Validate file size
    if (file.size > MAX_IMAGE_SIZE_MB * 1024 * 1024) {
      dispatch(
        setAlert({
          open: true,
          message: `File size exceeds ${MAX_IMAGE_SIZE_MB} MB.`,
          severity: enums.SEVERITY.ERROR,
        })
      );
      return;
    }

    const updatedImagePreview = { ...imagePreview, open: true, value: file };
    let updatedAttachmentObj = {
      ...values.attachment,
      file,
      isUploading: true,
    };
    formik.setFieldValue("attachment", updatedAttachmentObj);
    setImagePreview(updatedImagePreview);
  };

  const getImageDimensions = (file) => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => {
        resolve({ width: img.width, height: img.height });
      };
      img.onerror = reject;
      img.src = URL.createObjectURL(file);
    });
  };

  const uploadImageHandler = async (file) => {
    if (file.size > 1048576) {
      dispatch(
        setAlert({
          open: true,
          message: "File size should not exceed 1 MB.",
          severity: enums.SEVERITY.ERROR,
        })
      );
      return;
    }

    // Validate image dimensions
    const imageDimensions = await getImageDimensions(file);
    if (imageDimensions.width > 80 || imageDimensions.height > 80) {
      dispatch(
        setAlert({
          open: true,
          message: "Image dimensions should not exceed 80 x 80 pixels.",
          severity: enums.SEVERITY.ERROR,
        })
      );
      return;
    }

    const fileInBase64 = await fileToBase64(file);
    imgUpload.mutate(
      {
        image: fileInBase64,
        folder: "logo",
      },
      {
        onSuccess: (response) => {
          // console.log(response)
          setImageUploadPending(false);
          const updatedAttachmentObj = {
            ...values.attachment,
            url: response.data.data,
          };
          formik.setFieldValue("attachment", updatedAttachmentObj);
        },
      }
    );
    closePreviewModal();
    setImageUploadPending(true);
  };

  const cancelImageUpload = (currentSavedImg) => {
    if (currentSavedImg) {
      formik.setFieldValue("attachment", { url: currentSavedImg });
    } else formik.setFieldValue("attachment", {});
    closePreviewModal();
  };

  const closePreviewModal = () =>
    setImagePreview({ ...imagePreview, open: false });

  const [imagePreview, setImagePreview] = useState({
    open: false,
    value: "",
    imagePreviewBtns: [
      {
        btnLabel: "Upload",
        action: uploadImageHandler,
        params: true,
      },
      {
        btnLabel: "Cancel",
        action: cancelImageUpload,
        params: true,
      },
    ],
  });

  const { data: paymentDetails, isFetching: isPaymentDetailsFetching } =
    usePaymentDetailsOfCompany(canViewFinanceConfig);
  const { data: teams, isFetching, refetch: refetchTeam } = useQuery(
    queryKeys.team(id),
    fetchWithError,
    { enabled: !is.empty(id), select: selector }
  );
  const queryClient = useQueryClient();
  const { mutate, isLoading } = useMutate("team");

  const handleDisableSubscription = useCallback((licensePk) => {
    if (!licensePk) {
      console.error('licensePk is undefined or null');
      dispatch(
        setAlert({
          open: true,
          message: "License ID is missing. Please refresh the page and try again.",
          severity: enums.SEVERITY.ERROR,
        })
      );
      return;
    }

    mutate(
      {
        url: teamChargerLicenseStatusUrl(id),
        method: "PATCH",
        body: { license: licensePk }
      },
      {
        onSuccess: () => {
          // Refetch team data which includes subscription data
          queryClient.invalidateQueries(queryKeys.team(id));
          dispatch(
            setAlert({
              open: true,
              message: "Subscription status updated successfully.",
              severity: enums.SEVERITY.SUCCESS,
            })
          );
        },
        onError: (error) => {
          console.error("Failed to update subscription status:", error);
          dispatch(
            setAlert({
              open: true,
              message: "Failed to update subscription status. Please try again.",
              severity: enums.SEVERITY.ERROR,
            })
          );
        }
      }
    );
  }, [mutate, id, queryClient, dispatch, setAlert]);

  const handleSendBulkNotifications = useCallback(() => {
    setBulkNotificationModalOpen(true);
  }, []);

  const handleCloseBulkNotificationModal = useCallback(() => {
    setBulkNotificationModalOpen(false);
  }, []);

  const handleOpenAddLicenseModal = useCallback(() => {
    // Check license limit before opening modal
    if (!is.empty(teams)) {
      const [team] = teams;
      const allowedLicenses = team?.financeConfig?.charger_payment_details?.allowed_licenses || 0;
      const currentLicenseCount = team?.chargerLicenses?.length || 0;
      if (allowedLicenses <= currentLicenseCount) {
        dispatch(
          alertSlice.actions.setAlert({
            open: true,
            message: "You have reached the charger license limit. Please increase the number of allowed licenses to continue.",
            severity: enums.SEVERITY.WARNING,
          })
        );
        return; // Prevent modal from opening
      }
    }

    setAddLicenseModalOpen(true);
  }, [teams, dispatch]);

  const handleCloseAddLicenseModal = useCallback(() => {
    setAddLicenseModalOpen(false);
    // Refresh team data after adding licenses using existing pattern
    if (teams && teams.length > 0) {
      refetchTeam();
    }
  }, [refetchTeam, teams]);

  const onSubmit = async (teamData, { setSubmitting }) => {
    const sanitizable = sanitizeValues(teamData);
    const mutateObject = {
      url: teamUrl,
      method: "POST",
    };
    const {
      name,
      description,
      active,
      city,
      country,
      state,
      street,
      zipCode,
      address,
      locationLongitude,
      locationLatitude,
      payoutAccounts,
      financeConfig,
      attachment,
      advance_settings,
      communication
    } = sanitizable;

    if (is.empty(locationLatitude) || is.empty(locationLongitude)) {
      dispatch(
        setAlert({
          open: true,
          message:
            "Please specify an address, lattitude and longitude cant be empty",
          severity: enums.SEVERITY.ERROR,
        })
      );
      setSubmitting(false);
      return;
    }


    const finalPayoutAccounts = payoutAccounts ? payoutAccounts.reduce((finalPayoutAccounts, payoutAccount) => {
      const noNullValues = isObjectWithNonNullValues(payoutAccount, [
        "gst",
      ]);

      if (noNullValues) {
        const {
          business_name,
          business_type,
          beneficiary_name,
          email,
          phone,
          account_no,
          ifsc_code,
          gst,
          payoutDetailId,
          tax_percentage,
          payout_percentage,
          account_type,
          is_default,
        } = payoutAccount;
        const updatedPayoutAccount = {
          business_name: business_name.trim(),
          business_type: business_type.trim(),
          beneficiary_name: beneficiary_name.trim(),
          email: email.trim(),
          phone: phone.trim(),
          account_no: account_no.trim(),
          ifsc_code: ifsc_code.trim(),
          gst: gst.trim(),
          account_type: account_type.trim(),
          payout_percentage,
          tax_percentage,
          payoutDetailId: payoutDetailId ?? null,
          is_default,
        };

        finalPayoutAccounts = [
          ...finalPayoutAccounts,
          updatedPayoutAccount,
        ];
      }
      return finalPayoutAccounts;
    }, []) : [];
    const teamReqBody = {
      name,
      description,
      active,
      parentCompanyId,
      address: {
        ...address,
        city,
        country,
        zipCode,
        state,
        street,
        lat: locationLatitude,
        lng: locationLongitude,
      },
      attachment,
      enable_license_payment: values.advance_settings?.enable_license_payment || !1,
      erp_company_id: null,
      payoutAccounts: finalPayoutAccounts,
      financeConfig,
      communication
    };

    if (!canEditFinanceConfig) {
      delete teamReqBody.financeConfig;
    }

    if (canEditFinanceConfig && isParentCompanyLogin) {
      delete teamReqBody.financeConfig;
    }

    const { erp_company_id, erp_company_name } = advance_settings;
    if (erp_company_id) {
      teamReqBody.erp_company_id = erp_company_id;
    }

    if (erp_company_name) {
      teamReqBody.erp_company_name = erp_company_name
    }

    if (!is.nullOrUndefined(id) && !isCreateTeam) {
      mutateObject.method = "PUT";
      mutateObject.url = teamUrlId(id);
    }
    mutate(
      {
        url: mutateObject.url,
        method: mutateObject.method,
        body: teamReqBody,
      },
      { onSuccess }
    );
    setSubmitting(false);
  };

  const profileRefresh = async () => {
    try {
      await queryClient.fetchQuery({
        queryKey: queryKeys.profile(),
        queryFn: fetchWithError,
        staleTime: 0
      });
      
    } catch (error) {
      console.error("Error calling profile API:", error);
    }
  }

  const onSuccess = async () => {
    await profileRefresh();
    dispatch(
      tabsCurrentlyOpenedForSlice.actions.setTabsOpened({
        detailsId: "",
        tabOpenedFor: "",
        tabOpened: "",
      })
    );
    navigate("/team");
  };

  const formik = useFormik({
    initialValues: {
      ...getTeamDefaultValues(),
      enable_license_payment: company?.enable_license_payment ?? false,
      ...(canViewFinanceConfig
        ? {}
        : {
          financeConfig: undefined,
          payoutAccounts: [initialPayoutAccountObj],
        }),
    },
    validationSchema: teamFormValidationSchema,
    onSubmit,
  });

  const {
    errors,
    values,
    touched,
    handleSubmit,
    isSubmitting,
    getFieldProps,
    resetForm,
    setFieldValue,
    handleChange,
  } = formik;

  useEffect(() => {
    // When company license flag changes to true, allow team to toggle
    if (
      company &&
      company.enable_license_payment === true &&
      values.enable_license_payment === false
    ) {
      setFieldValue("enable_license_payment", false);
    }
  }, [company?.enable_license_payment, setFieldValue]);

  useEffect(() => {
    paymentDetails && setTaxLabel(company?.taxBillingConfig?.taxLabel || "Tax");
  }, [paymentDetails]);

  useEffect(() => {
    !is.empty(teamUsers) && setTeamUsers(teamUsers);
  }, [teamUsers]);

  // Process real subscription data from team details API
  useEffect(() => {
    if (selectedTab === 'subscription' && !is.empty(teams)) {
      setIsSubscriptionLoading(true);

      const [team] = teams;
      if (team && team.chargerLicenses) {
        const subscriptionData = team.chargerLicenses.map((license, index) => ({
          id: license?.licensePk || index + 1,
          licensePk: license?.licensePk,
          licenseId: license?.licenseId,
          status: license?.status,
          teamName: team?.name,
          chargerId: license?.chargeBoxId,
          chargerCreationDate: license?.createdAt,
          chargerSubscriptionExpiryDate: license?.expiry,
          duration: license?.duration,
          lastRenewalDate: license?.createdAt,
          final_expiry: license?.final_expiry,
          contractSummary: license?.summary,
          remarks: license?.remarks,
        }));

        setSubscriptionData(subscriptionData);
      } else {
        setSubscriptionData([]);
      }
      setIsSubscriptionLoading(false);
    }
  }, [selectedTab, teams]);

  useEffect(() => {
    if (!is.empty(teams)) {
      const [team] = teams;
      const { address, payoutAccounts, financeConfig, url, erpCompanyId, erpCompanyName } = team;
      const { lat, lng } = address;

      const newValues = {
        ...team,
        ...address,
        locationLatitude: lat,
        locationLongitude: lng,
        attachment: { url: url },
        advance_settings: {
          erp_company_id: null,
          erp_company_name: null
        }
      };

      if (canViewFinanceConfig) {
        newValues.financeConfig = is.empty(financeConfig)
          ? initialFinanceConfigObj
          : financeConfig;
      } else {
        delete newValues.financeConfig;
      }

      // Always populate payoutAccounts regardless of finance access
      newValues.payoutAccounts = is.empty(payoutAccounts)
        ? [initialPayoutAccountObj]
        : payoutAccounts;

      if (erpCompanyId && erpCompanyName) {
        newValues.advance_settings.erp_company_id = erpCompanyId;
        newValues.advance_settings.erp_company_name = erpCompanyName;
      }

      if (!newValues.communication) {
        newValues.communication = communicationDefaultValue().communication;
      }
      resetForm({ values: newValues });
    }
  }, [teams, resetForm, canViewFinanceConfig]);

  const handleTabChange = (e, tab) => {
    setSelectedTab(tab);
  };

  const handleManageUsersOpen = useCallback(() => {
    dispatch(dialogSlice.actions.setDialog({ open: true, id }));
  }, [dispatch, id]);

  const handleMangageUsersClose = useCallback(() => {
    refetchMembers();
    dispatch(dialogSlice.actions.setDialog({ open: false, id: null }));
  }, [dispatch, refetchMembers]);

  const handleSubscriptionDetailsOpen = useCallback((subscriptionRow) => {
    setSelectedSubscriptionData(subscriptionRow);
    setSubscriptionModalOpen(true);
  }, []);

  const handleSubscriptionDetailsClose = useCallback(() => {
    setSubscriptionModalOpen(false);
    setSelectedSubscriptionData(null);
  }, []);

  const handleBankAccountDelete = useCallback(
    (index) => {
      const numberOfBankDetails = (values.payoutAccounts || []).length;
      if (numberOfBankDetails === 1) {
        dispatch(
          setAlert({
            open: true,
            message: "At least one payout account shall be defined",
            severity: enums.SEVERITY.ERROR,
          })
        );
        return;
      }
      const updatedBankDetailsFields = (values.payoutAccounts || []).filter(
        (_, ind) => index !== ind
      );
      setFieldValue("payoutAccounts", updatedBankDetailsFields);
      // setFieldValue("disabled",true);
      return;
    },
    [values.payoutAccounts, setFieldValue, dispatch, setAlert]
  );

  const handleBankAccountAddition = useCallback(() => {
    const updatedBankDetailsFields = [
      ...(values.payoutAccounts || []),
      initialPayoutAccountObj,
    ];
    setFieldValue("payoutAccounts", updatedBankDetailsFields);
    return;
  }, [values.payoutAccounts, setFieldValue]);

  const handlePayoutAccountEditOpen = useCallback((index) => {
    setPayoutAccountModalState({ open: true, index });
  }, []);

  const handlePayoutAccountEditClose = useCallback(() => {
    setPayoutAccountModalState({ open: false, index: null });
  }, []);

  const handlePayoutAccountEditSave = useCallback(
    (updatedAccountValues) => {
      if (is.nullOrUndefined(payoutAccountModalState.index)) {
        handlePayoutAccountEditClose();
        return;
      }

      const sanitizedAccount = Object.entries(updatedAccountValues || {}).reduce(
        (acc, [key, value]) => {
          acc[key] = typeof value === "string" ? value.trim() : value;
          return acc;
        },
        {}
      );

      const updatedAccounts = (values.payoutAccounts || []).map((account, idx) => {
        if (idx === payoutAccountModalState.index) {
          return {
            ...account,
            ...sanitizedAccount,
          };
        }
        // If the updated account is being set as default, unset all others
        if (sanitizedAccount.is_default === 1) {
          return {
            ...account,
            is_default: 0,
          };
        }
        return account;
      });

      setFieldValue("payoutAccounts", updatedAccounts);
      handlePayoutAccountEditClose();
    },
    [
      values.payoutAccounts,
      setFieldValue,
      payoutAccountModalState.index,
      handlePayoutAccountEditClose,
    ]
  );

  useEffect(() => {
    if (isCreateTeam && company) {
      formik.setFieldValue(
        "enable_license_payment",
        !!company.enable_license_payment
      );
    }
  }, [company, isCreateTeam]);

  // Set team name in store for the BackButton display
  useEffect(() => {
    if (!is.empty(teams) && !isCreateTeam) {
      const [team] = teams;
      if (team && team.name) {
        dispatch(tabsCurrentlyOpenedForSlice.actions.setTabsOpened({
          detailsId: team.name,
          tabOpenedFor: "team",
          tabOpened: selectedTab
        }));
      }
    }
  }, [teams, isCreateTeam, selectedTab, dispatch]);

  const payoutAccountBeingEdited =
    typeof payoutAccountModalState.index === "number"
      ? values?.payoutAccounts?.[payoutAccountModalState.index]
      : null;

  if (isFetching) {
    return <Loader />;
  }

  if (!isCreateTeam && is.empty(teams)) {
    navigate("/team");
    return null;
  }

  return (
    <>
      <TabContext value={selectedTab}>
        <Stack
          direction="row"
          justifyContent="center"
          alignItem="center"
          sx={{ width: "100%" }}
        >
          <RootTabStyle
            indicatorColor="transparent"
            centered
            length={tabsToShow.length}
            onChange={handleTabChange}
          >
            {tabsToShow.map((tab) => (
              <RootTabItemStyles
                key={tab.value}
                value={tab.value}
                label={tab.label}
              />
            ))}
          </RootTabStyle>
        </Stack>

        <Card sx={{ p: 2 }}>
          <FormikProvider value={formik}>
            <Form
              autoComplete="off"
              id="team"
              noValidate
              onSubmit={handleSubmit}
            >
              <TabPanel value="general">
                <Grid container spacing={3} className="cms-grid-mb">
                  <Grid item xs={12} lg={6}>
                    <FormControl fullWidth size="small">
                      <TextField
                        id="name"
                        label="Team Name"
                        size="small"
                        variant="outlined"
                        {...getFieldProps("name")}
                        disabled={isReadOnly}
                        error={Boolean(touched.name && errors.name)}
                        helperText={touched.name && errors.name}
                      />
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} lg={6}>
                    <FormControl fullWidth size="small">
                      <InputLabel id="demo-simple-select-label">
                        {" "}
                        Status{" "}
                      </InputLabel>
                      <Select
                        labelId="demo-simple-select-label"
                        id="demo-simple-select"
                        label="Status"
                        defaultValue={values.active}
                        disabled={isReadOnly}
                        {...getFieldProps("active")}
                      >
                        <MenuItem value={"Y"}>Active</MenuItem>
                        <MenuItem value={"N"}>Inactive</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} lg={6}>
                    <FormControl fullWidth size="medium">
                      <TextField
                        multiline
                        rows={6}
                        variant="outlined"
                        id="description"
                        label="Description"
                        {...getFieldProps("description")}
                        error={Boolean(
                          touched.description && errors.description
                        )}
                        helperText={touched.description && errors.description}
                      />
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} lg={6}>
                    <Box>
                      <Stack
                        direction="row"
                        justifyContent="space-between"
                        alignItems="center"
                        spacing={2}
                        sx={{ padding: "10px" }}
                      >
                        {imageUploadPending ? (
                          <Box
                            sx={{
                              height: "170px",
                              width: "250px",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                          >
                            <CircularProgress />
                          </Box>
                        ) : values.attachment &&
                          is.empty(values.attachment.url) ? (
                          <TextField
                            label="Upload WebApp icon"
                            value={!imageUploadPending ? "" : "LOADING...."}
                            placeholder={"No file chosen"}
                            onChange={handleChange}
                            helperText={`This Image will show as icon on WebApp.`}
                            disabled
                          />
                        ) : (
                          <div>
                            <Typography variant="body2" color="text.secondary">
                              Upload WebApp icon
                            </Typography>
                            {values?.attachment?.url && (
                              <Card
                                style={{
                                  border: "1px solid #ddd",
                                  marginBottom: 10,
                                  boxShadow: "none",
                                  width: "400px",
                                }}
                              >
                                <CardContent
                                  sx={{
                                    display: "flex",
                                    placeContent: "center",
                                  }}
                                >
                                  <ImageList cols={1}>
                                    <ImageListItem
                                      key={`${values?.attachment?.url}-key`}
                                    >
                                      <img
                                        src={values?.attachment?.url}
                                        alt={"attachment"}
                                        style={{
                                          maxHeight: "100px",
                                          maxWidth: "100px",
                                        }}
                                        loading="eager"
                                        onClick={() => {
                                          setPhotoModal({
                                            open: true,
                                            src: values?.attachment?.url,
                                          });
                                        }}
                                      />
                                    </ImageListItem>
                                  </ImageList>
                                </CardContent>
                              </Card>
                            )}
                            <Typography variant="body2" color="text.secondary">
                              This Image will show as icon on WebApp.
                            </Typography>
                          </div>
                        )}
                        {!isReadOnly && (
                          <LoadingButton
                            variant="contained"
                            onClick={() => imageUploadInputRef.current.click()}
                            loading={imageUploadPending}
                            loadingIndicator="Loading…"
                            endIcon={<Icon icon="uil:image-upload"></Icon>}
                          >
                            Upload
                            <input
                              accept="image/jpeg,image/png,image/jpg"
                              type="file"
                              hidden
                              id="issue-image"
                              ref={imageUploadInputRef}
                              onChange={handleImageUpload}
                            />
                          </LoadingButton>
                        )}
                      </Stack>
                    </Box>
                  </Grid>
                  {company?.advance_settings &&
                    <Grid item xs={12}>
                      <CompanyEnableFeatures
                        getFieldProps={getFieldProps}
                        values={values}
                        setFieldValue={setFieldValue}
                        isTeam={true}
                        companyId={company.advance_settings.erp_company_id}
                        parentLicenseStatus={company?.enable_license_payment}
                        showMapping={!!company.advance_settings.erp_company_id}
                      />
                    </Grid>}
                  {!isCreateTeam && values.advance_settings.erp_company_name &&
                    <Grid item xs={12}>
                      <Communicate
                        getFieldProps={getFieldProps}
                        values={values}
                        setFieldValue={setFieldValue}
                        isTeam={true}
                        advanceSettingValue={values}
                        containerStyle={{ padding: 0 }}
                      />
                    </Grid>}
                </Grid>
              </TabPanel>
              <TabPanel value="payout accounts">
                {(isCreateTeam || isGPCLogin || isParentCompanyLogin) && !isReadOnly && (
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="right"
                    mb={3}
                  >
                    <LoadingButton
                      size="medium"
                      variant="contained"
                      startIcon={<Icon icon={AddIcon}></Icon>}
                      // loading={isSubmitting || isLoading}
                      onClick={handleBankAccountAddition}
                    >
                      Add Account
                    </LoadingButton>
                  </Stack>
                )}
                <Stack>
                  {(values.payoutAccounts || []).map(
                    (payoutAccountObj, index, arr) => {
                      const { business_type, creation, deleteBtn } =
                        payoutAccountObj;

                      // if(disabled) return<></>;

                      return (
                        <Grid
                          container
                          spacing={3}
                          my={3}
                          className="cms-grid-mb"
                          key={`payout-account-${index}`}
                        >
                          <Grid item xs={12} lg={12}>
                            <Stack
                              direction="row"
                              alignItems="baseline"
                              justifyContent="space-between"
                              paddingBottom={2}
                              sx={{ borderBottom: "1px solid grey" }}
                            >
                              <Stack direction="row" alignItems="center" spacing={1}>
                                <Typography>
                                  {`PAYOUT ACCOUNT ${index + 1}`}
                                </Typography>
                                {payoutAccountObj.is_default === 1 && (
                                  <Chip
                                    label="DEFAULT"
                                    size="small"
                                    color="primary"
                                    variant="outlined"
                                    sx={{ mb: 2 }}
                                  />
                                )}
                              </Stack>
                              <Stack direction="row" spacing={1}>
                                {!creation && !isReadOnly && (
                                  <LoadingButton
                                    size="small"
                                    variant="outlined"
                                    startIcon={<Icon icon="mdi:pencil-outline"></Icon>}
                                    onClick={() => handlePayoutAccountEditOpen(index)}
                                  >
                                    {`Edit PAYOUT ACCOUNT ${index + 1}`}
                                  </LoadingButton>
                                )}
                                {deleteBtn && arr.length > 1 && !isReadOnly && (
                                  <LoadingButton
                                    size="small"
                                    variant="contained"
                                    color="error"
                                    startIcon={<Icon icon={DeleteIcon}></Icon>}
                                    // loading={isSubmitting || isLoading}
                                    onClick={() => handleBankAccountDelete(index)}
                                  >
                                    Delete
                                  </LoadingButton>
                                )}
                              </Stack>
                            </Stack>
                            {/* <Divider variant="middle" /> */}
                          </Grid>
                          {creation && (
                            <Grid item xs={12} lg={12} md={12}>
                              <Typography variant="caption">
                                **Either fill the form completely (excluding{" "}
                                {taxLabel}) or keep form completely empty
                              </Typography>
                            </Grid>
                          )}
                          <Grid item xs={12} lg={4} md={4}>
                            <FormControl fullWidth size="small">
                              <TextField
                                id={`business-name-${index}`}
                                label="Business / Individual Name"
                                size="small"
                                variant="outlined"
                                {...getFieldProps(
                                  `payoutAccounts[${index}].business_name`
                                )}
                                disabled={isReadOnly || !creation}
                                error={Boolean(
                                  touched &&
                                  touched.payoutAccounts &&
                                  errors &&
                                  errors.payoutAccounts &&
                                  touched.payoutAccounts[index] &&
                                  errors.payoutAccounts[index] &&
                                  touched.payoutAccounts[index]
                                    .business_name &&
                                  errors.payoutAccounts[index].business_name
                                )}
                                helperText={
                                  touched &&
                                  touched.payoutAccounts &&
                                  errors &&
                                  errors.payoutAccounts &&
                                  touched.payoutAccounts[index] &&
                                  errors.payoutAccounts[index] &&
                                  touched.payoutAccounts[index].business_name &&
                                  errors.payoutAccounts[index].business_name
                                }
                              />
                            </FormControl>
                          </Grid>
                          <Grid item xs={12} lg={4} md={4}>
                            <FormControl
                              size="small"
                              fullWidth
                              error={
                                touched &&
                                touched.payoutAccounts &&
                                errors &&
                                errors.payoutAccounts &&
                                touched.payoutAccounts[index] &&
                                errors.payoutAccounts[index] &&
                                touched.payoutAccounts[index].business_type &&
                                errors.payoutAccounts[index].business_type
                              }
                            >
                              <InputLabel
                                id={`business-type-input-label-${index}`}
                              >
                                Business Type
                              </InputLabel>
                              <Select
                                labelId={`business-type-input-label-${index}`}
                                id={`business-type-${index}`}
                                disabled={isReadOnly || !creation}
                                value={business_type}
                                onChange={(e) =>
                                  setFieldValue(
                                    `payoutAccounts[${index}].business_type`,
                                    e.target.value
                                  )
                                }
                                label="Business Type"
                                {...getFieldProps(
                                  `payoutAccounts[${index}].business_type`
                                )}
                              >
                                <MenuItem selected value="None" disabled>
                                  <em>None</em>
                                </MenuItem>
                                {possibleBusinessTypes.map(
                                  ({ label, value, disabled }, index) => {
                                    return (
                                      <MenuItem
                                        key={`transaction-status-${index}-${label}`}
                                        value={value}
                                        disabled={disabled}
                                      >
                                        {label}
                                      </MenuItem>
                                    );
                                  }
                                )}
                              </Select>
                            </FormControl>
                          </Grid>
                          <Grid item xs={12} lg={4} md={4}>
                            <FormControl fullWidth size="small">
                              <TextField
                                id={`beneficiary-name-${index}`}
                                label="Beneficiary Name"
                                size="small"
                                variant="outlined"
                                {...getFieldProps(
                                  `payoutAccounts[${index}].beneficiary_name`
                                )}
                                disabled={isReadOnly || !creation}
                                error={Boolean(
                                  touched &&
                                  touched.payoutAccounts &&
                                  errors &&
                                  errors.payoutAccounts &&
                                  touched.payoutAccounts[index] &&
                                  errors.payoutAccounts[index] &&
                                  touched.payoutAccounts[index]
                                    .beneficiary_name &&
                                  errors.payoutAccounts[index]
                                    .beneficiary_name
                                )}
                                helperText={
                                  touched &&
                                  touched.payoutAccounts &&
                                  errors &&
                                  errors.payoutAccounts &&
                                  touched.payoutAccounts[index] &&
                                  errors.payoutAccounts[index] &&
                                  touched.payoutAccounts[index]
                                    .beneficiary_name &&
                                  errors.payoutAccounts[index].beneficiary_name
                                }
                              />
                            </FormControl>
                          </Grid>
                          <Grid item xs={12} lg={4} md={4}>
                            <FormControl fullWidth size="small">
                              <TextField
                                id={`email-${index}`}
                                label="Email"
                                size="small"
                                variant="outlined"
                                {...getFieldProps(
                                  `payoutAccounts[${index}].email`
                                )}
                                disabled={isReadOnly || !creation}
                                error={Boolean(
                                  touched &&
                                  touched.payoutAccounts &&
                                  errors &&
                                  errors.payoutAccounts &&
                                  touched.payoutAccounts[index] &&
                                  errors.payoutAccounts[index] &&
                                  touched.payoutAccounts[index].email &&
                                  errors.payoutAccounts[index].email
                                )}
                                helperText={
                                  touched &&
                                  touched.payoutAccounts &&
                                  errors &&
                                  errors.payoutAccounts &&
                                  touched.payoutAccounts[index] &&
                                  errors.payoutAccounts[index] &&
                                  touched.payoutAccounts[index].email &&
                                  errors.payoutAccounts[index].email
                                }
                              />
                            </FormControl>
                          </Grid>
                          <Grid item xs={12} lg={4} md={4}>
                            <FormControl fullWidth size="small">
                              <TextField
                                id={`phone-${index}`}
                                label="Phone Number"
                                size="small"
                                variant="outlined"
                                {...getFieldProps(
                                  `payoutAccounts[${index}].phone`
                                )}
                                disabled={isReadOnly || !creation}
                                error={Boolean(
                                  touched &&
                                  touched.payoutAccounts &&
                                  errors &&
                                  errors.payoutAccounts &&
                                  touched.payoutAccounts[index] &&
                                  errors.payoutAccounts[index] &&
                                  touched.payoutAccounts[index].phone &&
                                  errors.payoutAccounts[index].phone
                                )}
                                helperText={
                                  touched &&
                                  touched.payoutAccounts &&
                                  errors &&
                                  errors.payoutAccounts &&
                                  touched.payoutAccounts[index] &&
                                  errors.payoutAccounts[index] &&
                                  touched.payoutAccounts[index].phone &&
                                  errors.payoutAccounts[index].phone
                                }
                              />
                            </FormControl>
                          </Grid>

                          <Grid item xs={12} lg={4} md={4}>
                            <FormControl fullWidth size="small">
                              <TextField
                                id={`account-no-${index}`}
                                label="Account Number"
                                size="small"
                                variant="outlined"
                                {...getFieldProps(
                                  `payoutAccounts[${index}].account_no`
                                )}
                                disabled={isReadOnly || !creation}
                                error={Boolean(
                                  touched &&
                                  touched.payoutAccounts &&
                                  errors &&
                                  errors.payoutAccounts &&
                                  touched.payoutAccounts[index] &&
                                  errors.payoutAccounts[index] &&
                                  touched.payoutAccounts[index].account_no &&
                                  errors.payoutAccounts[index].account_no
                                )}
                                helperText={
                                  touched &&
                                  touched.payoutAccounts &&
                                  errors &&
                                  errors.payoutAccounts &&
                                  touched.payoutAccounts[index] &&
                                  errors.payoutAccounts[index] &&
                                  touched.payoutAccounts[index].account_no &&
                                  errors.payoutAccounts[index].account_no
                                }
                              />
                            </FormControl>
                          </Grid>
                          <Grid item xs={12} lg={4} md={4}>
                            <FormControl fullWidth size="small">
                              <TextField
                                id={`account-no-${index}`}
                                label="Account Type"
                                size="small"
                                variant="outlined"
                                {...getFieldProps(
                                  `payoutAccounts[${index}].account_type`
                                )}
                                disabled={isReadOnly || !creation}
                                error={Boolean(
                                  touched &&
                                  touched.payoutAccounts &&
                                  errors &&
                                  errors.payoutAccounts &&
                                  touched.payoutAccounts[index] &&
                                  errors.payoutAccounts[index] &&
                                  touched.payoutAccounts[index]
                                    .account_type &&
                                  errors.payoutAccounts[index].account_type
                                )}
                                helperText={
                                  touched &&
                                  touched.payoutAccounts &&
                                  errors &&
                                  errors.payoutAccounts &&
                                  touched.payoutAccounts[index] &&
                                  errors.payoutAccounts[index] &&
                                  touched.payoutAccounts[index].account_type &&
                                  errors.payoutAccounts[index].account_type
                                }
                              />
                            </FormControl>
                          </Grid>
                          <Grid item xs={12} lg={4} md={4}>
                            <FormControl fullWidth size="small">
                              <TextField
                                id={`ifsc-code-${index}`}
                                label="IFSC Code"
                                size="small"
                                variant="outlined"
                                {...getFieldProps(
                                  `payoutAccounts[${index}].ifsc_code`
                                )}
                                disabled={isReadOnly || !creation}
                                error={Boolean(
                                  touched &&
                                  touched.payoutAccounts &&
                                  errors &&
                                  errors.payoutAccounts &&
                                  touched.payoutAccounts[index] &&
                                  errors.payoutAccounts[index] &&
                                  touched.payoutAccounts[index].ifsc_code &&
                                  errors.payoutAccounts[index].ifsc_code
                                )}
                                helperText={
                                  touched &&
                                  touched.payoutAccounts &&
                                  errors &&
                                  errors.payoutAccounts &&
                                  touched.payoutAccounts[index] &&
                                  errors.payoutAccounts[index] &&
                                  touched.payoutAccounts[index].ifsc_code &&
                                  errors.payoutAccounts[index].ifsc_code
                                }
                              />
                            </FormControl>
                          </Grid>
                          <Grid item xs={12} lg={4} md={4}>
                            <FormControl fullWidth size="small">
                              <TextField
                                id={`bank-address-${index}`}
                                label="GST Number"
                                size="small"
                                variant="outlined"
                                {...getFieldProps(
                                  `payoutAccounts[${index}].gst`
                                )}
                                disabled={isReadOnly || !creation}
                                error={Boolean(
                                  touched &&
                                  touched.payoutAccounts &&
                                  errors &&
                                  errors.payoutAccounts &&
                                  touched.payoutAccounts[index] &&
                                  errors.payoutAccounts[index] &&
                                  touched.payoutAccounts[index].gst &&
                                  errors.payoutAccounts[index].gst
                                )}
                                helperText={
                                  touched &&
                                  touched.payoutAccounts &&
                                  errors &&
                                  errors.payoutAccounts &&
                                  touched.payoutAccounts[index] &&
                                  errors.payoutAccounts[index] &&
                                  touched.payoutAccounts[index].gst &&
                                  errors.payoutAccounts[index].gst
                                }
                              />
                            </FormControl>
                          </Grid>
                        </Grid>
                      );
                    }
                  )}
                </Stack>
              </TabPanel>
              <TabPanel value="address">
                <Address
                  getFieldProps={getFieldProps}
                  setFieldValue={setFieldValue}
                  formValues={values}
                  notDisplayTitle={true}
                  displayedFor="team"
                />
              </TabPanel>
              <TabPanel value="members">
                {!isCreateTeam && (
                  <Card
                    variant="outlined"
                    sx={{ p: 1 }}
                    style={{ marginBottom: "20px", padding: "35px 20px" }}
                  >
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "row",
                        justifyContent: "space-between",
                        margin: "20px",
                      }}
                    >
                      <h2 style={{ margin: "5px" }}>
                        {" "}
                        Team members ({teamUsersData.length}){" "}
                      </h2>
                      <LoadingButton
                        size="small"
                        variant="contained"
                        endIcon={<Icon icon={PersonOutline}></Icon>}
                        disabled={isReadOnly}
                        onClick={handleManageUsersOpen}
                      >
                        Manage members
                      </LoadingButton>
                    </div>
                    <BaseTable
                      rows={teamUsersData ?? []}
                      columns={teamMembersColumns(false, false)}
                      getRowId={(row) => row.userId}
                      loading={isUserFetching || isRefetchingMembers}
                      pageSize={15}
                    />
                  </Card>
                )}
              </TabPanel>
              <TabPanel value="finance">
                {!isPaymentDetailsFetching ? (
                  <TeamFinanceForm
                    formik={formik}
                    permissions={financeFormPermissions}
                    taxLabel={taxLabel}
                    paymentDetails={paymentDetails ?? {}}
                  />
                ) : (
                  <Loader />
                )}
              </TabPanel>
              {/* <TabPanel value="payout invoices">
                <TeamPayoutInvoices />
              </TabPanel> */}
              <TabPanel value="subscription">
                <Stack
                  direction="row"
                  justifyContent="flex-end"
                  sx={{ mb: 2 }}
                >
                  {!isReadOnly && (
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={handleOpenAddLicenseModal}
                      disabled={isCreateTeam || !id}
                      startIcon={<Icon icon="mdi:autorenew" />}
                    >
                      Renewal License
                    </Button>
                  )}
                </Stack>
                {!isCreateTeam && (
                  <>
                    {/* Header with Send Bulk Notifications button */}
                    {canEditFinanceConfig && subscriptionData.length > 0 && (() => {
                      // Check if any license is expiring within 7 days
                      const hasExpiringLicenses = subscriptionData.some(subscription => {
                        if (!subscription.chargerSubscriptionExpiryDate) return false;

                        const expiryDate = new Date(subscription.chargerSubscriptionExpiryDate);
                        const today = new Date();
                        const timeDifference = expiryDate.getTime() - today.getTime();
                        const daysDifference = Math.ceil(timeDifference / (1000 * 3600 * 24));

                        return daysDifference <= 7 && daysDifference >= 0;
                      });

                      return hasExpiringLicenses && (
                        <Stack
                          direction="row"
                          justifyContent="space-between"
                          alignItems="center"
                          sx={{ mb: 2 }}
                        >
                          <Typography variant="h6">
                            Charger Subscriptions
                          </Typography>
                          <LoadingButton
                            variant="contained"
                            color="warning"
                            startIcon={<Icon icon="eva:bell-fill" />}
                            onClick={handleSendBulkNotifications}
                          >
                            Send Bulk Notifications
                          </LoadingButton>
                        </Stack>
                      );
                    })()}

                    <BaseTable
                      rows={subscriptionData ?? []}
                      columns={subscriptionDetailsColumns(
                        handleSubscriptionDetailsOpen,
                        !isParentCompanyLogin,
                        handleDisableSubscription,
                        canEditFinanceConfig
                      )}
                      getRowId={(row) => row.id}
                      loading={isSubscriptionLoading}
                      pageSize={15}
                    />
                  </>
                )}
                {isCreateTeam && (
                  <Card
                    variant="outlined"
                    sx={{ p: 3, textAlign: 'center' }}
                  >
                    <Typography variant="h6" color="text.secondary">
                      Subscription details will be available after the team is created.
                    </Typography>
                  </Card>
                )}
              </TabPanel>
              {selectedTab !== "members" &&
                selectedTab !== "payout invoices" &&
                selectedTab !== "payout schedule" &&
                selectedTab !== "subscription" && (
                  <Stack
                    direction="row"
                    spacing={2}
                    justifyContent="center"
                    alignItems="center"
                  >
                    {!isReadOnly && (
                      <>
                        <LoadingButton
                          size="large"
                          type="submit"
                          variant="contained"
                          disabled={isReadOnly}
                          loading={isSubmitting || isLoading}
                        >
                          Save
                        </LoadingButton>
                        <LoadingButton
                          size="large"
                          type="button"
                          variant="outlined"
                          component={RouterLink}
                          to="/team"
                        >
                          Cancel
                        </LoadingButton>
                      </>
                    )}
                  </Stack>
                )}
            </Form>
          </FormikProvider>
          {/* Payout schedule tab - separate from main form to avoid nesting */}
          <TabPanel value="payout schedule">
            {!isCreateTeam && (
              <TeamPayoutScheduleTab
                teamId={id}
                parentCompanyId={parentCompanyId}
                isReadOnly={isReadOnly}
              />
            )}
            {isCreateTeam && (
              <Card
                variant="outlined"
                sx={{ p: 3, textAlign: 'center' }}
              >
                <Typography variant="h6" color="text.secondary">
                  Payout schedule will be available after the team is created.
                </Typography>
              </Card>
            )}
          </TabPanel>
        </Card>
      </TabContext>
      <TeamPayoutAccountEditModal
        open={payoutAccountModalState.open}
        onClose={handlePayoutAccountEditClose}
        accountIndex={payoutAccountModalState.index}
        account={payoutAccountBeingEdited}
        onSave={handlePayoutAccountEditSave}
        businessTypes={possibleBusinessTypes}
        taxLabel={taxLabel}
        allAccounts={values.payoutAccounts || []}
      />
      {modalOpen && (
        <TeamUserModal
          open={modalOpen}
          teamId={id}
          onClose={handleMangageUsersClose}
          enableRedirection={false}
        />
      )}
      {imagePreview.open && (
        <ImgPreviewModal
          imageObj={imagePreview}
          buttonsArray={imagePreview.imagePreviewBtns}
          currentSavedImg={values?.attachment?.url}
        />
      )}

      {photoModal && (
        <Dialog
          onClose={() => {
            setPhotoModal({ open: false, src: "" });
          }}
          aria-labelledby="customized-dialog-title"
          open={photoModal.open}
        >
          <IconButton
            aria-label="close"
            onClick={() => {
              setPhotoModal({ open: false, src: "" });
            }}
            sx={{
              position: "absolute",
              right: 0,
              top: 0,
            }}
          >
            <GridCloseIcon />
          </IconButton>
          <DialogContent
            sx={{
              minHeight: "300px",
              minWidth: "600px",
              display: "flex",
              placeItems: "center",
              placeContent: "center",
              padding: "32px 40px",
            }}
          >
            <img src={photoModal.src} alt="Review" />
          </DialogContent>
        </Dialog>
      )}

      <SubscriptionDetailsModal
        open={subscriptionModalOpen}
        onClose={handleSubscriptionDetailsClose}
        subscriptionData={selectedSubscriptionData}
        teamId={id}
        onUpdate={() => {
          queryClient.invalidateQueries(queryKeys.team(id));
        }}
      />

      <BulkNotificationModal
        open={bulkNotificationModalOpen}
        onClose={handleCloseBulkNotificationModal}
        subscriptionData={subscriptionData}
        teamId={id}
      />

      <AddLicenseModal
        open={addLicenseModalOpen}
        onClose={handleCloseAddLicenseModal}
        onLicenseAdded={refetchTeam}
      />
    </>
  );
};
